import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { EntityStore } from '../data/entity-store';

/**
 * Singleton in-memory data store backing every mock repository.
 *
 * Feature mock repositories request a typed `EntityStore<T>` by string key via
 * `collection<T>(name, seed)`. Stores are lazily created on first access and
 * shared across the app's lifetime, so writes from one screen are visible to
 * other screens in the same dev session.
 *
 * **No feature code should inject `MockDataService` directly** - only feature
 * mock repositories do. Feature services depend on the repository token, which
 * `provideRepository(...)` wires to either the mock or HTTP implementation
 * based on `environment.useMockData`. This keeps the API-swap path mechanical.
 *
 * Hard-fails if instantiated in a production build.
 */
@Injectable({ providedIn: 'root' })
export class MockDataService {
  private readonly stores = new Map<string, EntityStore<{ id: number }>>();

  constructor() {
    if (environment.production) {
      throw new Error('MockDataService must not be used in production - enable real API services.');
    }
  }

  /**
   * Return the entity store for `name`, creating it on first access using the
   * provided seed. Subsequent calls ignore the seed and return the existing
   * store, so seeding happens once per app lifetime.
   */
  collection<T extends { id: number }>(name: string, seed: () => readonly T[] = () => []): EntityStore<T> {
    let store = this.stores.get(name) as EntityStore<T> | undefined;
    if (!store) {
      store = new EntityStore<T>(seed());
      this.stores.set(name, store as EntityStore<{ id: number }>);
    }
    return store;
  }
}
